/**
 * `/quotes/:id/preview` — the proposal as the customer sees it, opened by the
 * office (`docs/proposal.md` §10.2, "Open as the customer").
 *
 * **Its own route rather than a tab**, and deliberately outside the office
 * frame: the whole point is to see the page with nothing of ours around it —
 * no left nav, no scope switcher, no page bar — which is also what makes it
 * worth texting to yourself and opening on a phone. The one thing added is a
 * dismissible banner saying nothing here is saved, because a page that is
 * indistinguishable from the real one is a page somebody will try to sign.
 *
 * It needs an **office** session (`GET /quotes/:id/proposal`, which is allowed
 * for a draft) and never a portal one: a customer reaching this address has
 * no business reading a draft, and the session check is what says so.
 *
 * Signing is off here — `onApprove` is absent, so the sign card is not drawn
 * at all rather than drawn disabled. Selections are kept in memory and thrown
 * away.
 */
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { ProposalSelection, ProposalView } from '@rowted/shared'
import { request } from '../lib/api.js'
import { EmptyState } from '../components/primitives.js'
import { IconClose } from '../frame/icons.js'
import { Proposal } from '../quote/proposal/index.js'

export function QuotePreview() {
  const { id = '' } = useParams()
  const [view, setView] = useState<ProposalView | null>(null)
  const [selection, setSelection] = useState<ProposalSelection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState(true)

  useEffect(() => {
    let live = true
    void request<{ proposal: ProposalView }>(`/quotes/${id}/proposal`)
      .then((body) => {
        if (!live) return
        setView(body.proposal)
        setSelection(body.proposal.initial)
      })
      .catch((cause: unknown) => {
        if (!live) return
        setError(cause instanceof Error ? cause.message : 'That proposal could not be opened.')
      })
    return () => { live = false }
  }, [id])

  if (error) {
    return (
      <div className="min-h-app bg-bg p-6">
        <EmptyState title="Not available" body={error} />
      </div>
    )
  }
  if (!view || !selection) {
    return (
      <div className="min-h-app bg-bg p-6">
        <p className="m-0 text-body text-text-muted">Loading the proposal…</p>
      </div>
    )
  }

  return (
    <Proposal
      view={view}
      selection={selection}
      onSelection={setSelection}
      banner={banner && (
        <div className="flex items-center gap-3 bg-surface-alt px-4 py-2 text-[12.5px]
                        text-text-body">
          <span className="min-w-0 flex-1">
            Preview — nothing you do here is saved.
          </span>
          <button
            type="button"
            aria-label="Hide this notice"
            onClick={() => setBanner(false)}
            className="shrink-0 text-text-muted hover:text-text"
          >
            <IconClose size={14} />
          </button>
        </div>
      )}
    />
  )
}
